import * as React from 'react';
import { fm } from 'Components/FormatMessage';
import { Item } from 'Components/Grid';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowListing from 'Components/HistoryRowListing';
import HistorySection from 'Components/HistorySection';
import { sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import { IHistoryContext } from 'Containers/FormContextHandler';
import DropDownButton from 'Components/_NewElements/DropDownButton';
import { parkinsonCriteriaTypes, TParkinsonCriteriaType } from 'Routes/Diagnosis/utils/definitions';
import PPADiagnosticCriteriaHistory from './PPA';
import FTDDiagnosticCriteriaHistory from './FTD';
import { interpretAlzheimerDiagnosticCriteriaABCD } from 'Routes/Diagnosis/utils/Alzheimer';
import { clinicallyEstablishedOrProbableOrPossible } from 'Routes/Diagnosis/utils';

const getDefaultCriteriaResult = (d: TParkinsonCriteriaType, type: string) => {
  if (
    clinicallyEstablishedOrProbableOrPossible(d, type) &&
    !['no', 'unknown'].includes(clinicallyEstablishedOrProbableOrPossible(d, type)!)
  ) {
    return fm(`diagnosis.${type}.${clinicallyEstablishedOrProbableOrPossible(d, type)}`);
  }
  if (clinicallyEstablishedOrProbableOrPossible(d, type)) {
    return fm(`diagnosis.${clinicallyEstablishedOrProbableOrPossible(d, type)}`);
  }
  return '-';
};

const ParkinsonDiagnosticsCriteria = ({ documents = [], startEdit, view }: IOwnProps): React.JSX.Element => {
  const docs: Array<TParkinsonCriteriaType> = documents.toSorted((d1, d2) => sortPartialDate(d2.date, d1.date));

  return (
    <HistorySection
      headerText={fm('diagnosis.parkinsonDiagnosticCriteria.title')}
      hasHistoryRows={false}
      newButton={
        <DropDownButton
          text="diagnosis.parkinsonDiagnosticCriteria.new"
          width={15}
          height={3}
          fontSize={14}
          buttons={parkinsonCriteriaTypes.map(type => ({
            text: `diagnosis.${type}.new`,
            onClick: startEdit ? startEdit({}, type) : () => {},
          }))}
          mainButtonOtherProps={{ alternate: true }}
          rounded={false}
        />
      }
    >
      <HistoryRowListing
        documents={docs}
        headers={
          <React.Fragment>
            <Item xs={2}>{fm('general.date')}</Item>
            <Item xs={3}>{fm('diagnosis.parkinsonDiagnosticCriteria.criteria')}</Item>
            <Item xs={4}>{fm('diagnosis.parkinsonDiagnosticCriteria.resultSummary')}</Item>
            <Item xs={3} />
          </React.Fragment>
        }
        contentFormat={(d: (typeof docs)[0]): React.JSX.Element => {
          const type = d._type;
          return (
            <React.Fragment>
              <Item xs={2} style={{ fontWeight: '600' }}>
                {formatPartialDate(d.date)}
              </Item>
              <Item xs={3}>{fm(`diagnosis.${type}.title`)}</Item>
              <Item xs={4}>
                {
                  // Select either "custom"...
                  {
                    psp: <></>,
                    ftd: <FTDDiagnosticCriteriaHistory d={d as IFTD} />,
                    ppa: <PPADiagnosticCriteriaHistory d={d as IPPA} />,
                    alzheimer: interpretAlzheimerDiagnosticCriteriaABCD(d)
                      ? fm(`diagnosis.${type}.interpretation.${interpretAlzheimerDiagnosticCriteriaABCD(d)}`)
                      : '-',
                  }[type] ||
                    // ...or default
                    getDefaultCriteriaResult(d, type)
                }
              </Item>
              <Item xs={3}>
                {startEdit ? <HistoryRowControls document={d} startEdit={startEdit} view={view} /> : null}
              </Item>
            </React.Fragment>
          );
        }}
      />
    </HistorySection>
  );
};

interface IOwnProps extends IHistoryContext<TParkinsonCriteriaType> {}

export default ParkinsonDiagnosticsCriteria;
